import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/utils/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
}

const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-moss-700 hover:bg-moss-600 text-ash-50 border border-moss-600/60',
  secondary:
    'bg-ash-900 hover:bg-ash-800 text-ash-100 border border-ash-700',
  ghost:
    'bg-transparent hover:bg-ash-900/60 text-ash-200 border border-transparent',
  danger:
    'bg-ember-800 hover:bg-ember-700 text-ash-50 border border-ember-700/60',
};

const SIZES: Record<Size, string> = {
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', className, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(
          'font-ui rounded-md tracking-wide uppercase',
          'transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-ash-950 focus:ring-moss-500',
          'disabled:opacity-40 disabled:cursor-not-allowed',
          VARIANTS[variant],
          SIZES[size],
          className,
        )}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';
